import React, { useState, useEffect } from 'react';
import { api } from '../services/api';

const STATUS_STYLE = {
  confirmed:  { bg: '#eff6ff', color: '#1d4ed8', label: 'Confirmed' },
  completed:  { bg: '#f0fdf4', color: '#15803d', label: 'Completed' },
  cancelled:  { bg: '#fef2f2', color: '#dc2626', label: 'Cancelled' },
  no_show:    { bg: '#fffbeb', color: '#b45309', label: 'No-show'   },
  pending:    { bg: '#f5f3ff', color: '#6d28d9', label: 'Pending'   },
};

function timeAgo(ts) {
  const s = Math.floor((Date.now() - new Date(ts)) / 1000);
  if (s < 60)  return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s/60)}m ago`;
  if (s < 86400) return `${Math.floor(s/3600)}h ago`;
  return `${Math.floor(s/86400)}d ago`;
}

function RestaurantActivity({ restaurant, onBack }) {
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.adminGetRestaurantActivity(restaurant.id)
      .then(d => { setRows(d); setLoading(false); })
      .catch(() => setLoading(false));
  }, [restaurant.id]);

  const counts = rows ? {
    total:     rows.length,
    confirmed: rows.filter(r => r.status === 'confirmed').length,
    completed: rows.filter(r => r.status === 'completed').length,
    cancelled: rows.filter(r => r.status === 'cancelled').length,
    no_show:   rows.filter(r => r.status === 'no_show').length,
  } : null;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 28 }}>
        <button style={s.backBtn} onClick={onBack}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6"/>
          </svg>
          Directory
        </button>
        <div style={s.dividerV} />
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={s.dot(restaurant.is_active !== 0)} />
            <h1 style={s.h1}>{restaurant.name}</h1>
          </div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>@{restaurant.username} · ID #{restaurant.id}</div>
        </div>
      </div>

      {loading && <div style={{ color: '#94a3b8', fontSize: 14, padding: '40px 0' }}>Loading…</div>}

      {!loading && rows && <>
        {/* Stats strip */}
        <div style={s.statsRow}>
          {[
            { label: 'Total bookings', value: counts.total },
            { label: 'Confirmed',      value: counts.confirmed,  color: '#1d4ed8' },
            { label: 'Completed',      value: counts.completed,  color: '#15803d' },
            { label: 'Cancelled',      value: counts.cancelled,  color: '#dc2626' },
            { label: 'No-shows',       value: counts.no_show,    color: '#b45309' },
          ].map(c => (
            <div key={c.label} style={s.statCard}>
              <div style={{ ...s.statVal, color: c.color || '#0f172a' }}>{c.value}</div>
              <div style={s.statLbl}>{c.label}</div>
            </div>
          ))}
        </div>

        {/* Activity list */}
        <div style={s.listCard}>
          <div style={s.ct}>Recent reservations</div>
          {rows.length === 0 ? (
            <div style={{ color: '#94a3b8', fontSize: 14, padding: '24px 0' }}>No reservations yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {rows.map((r, i) => {
                const st = STATUS_STYLE[r.status] || STATUS_STYLE.confirmed;
                return (
                  <div key={r.id} style={{ ...s.row, borderBottom: i < rows.length - 1 ? '1px solid #f1f5f9' : 'none' }}>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={s.name}>{r.customer_name}</span>
                        <span style={{ ...s.badge, background: st.bg, color: st.color }}>{st.label}</span>
                      </div>
                      <div style={s.meta}>
                        {r.date} · {r.time_slot} · {r.party_size} {r.party_size === 1 ? 'guest' : 'guests'}
                        {r.source && r.source !== 'manual' && <span style={s.src}>{r.source}</span>}
                      </div>
                      {(r.dietary_restrictions || r.special_requests || r.notes) && (
                        <div style={s.notes}>
                          {[r.dietary_restrictions, r.special_requests, r.notes].filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </div>
                    <div style={s.time}>{timeAgo(r.created_at)}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </>}

      {!loading && !rows && (
        <div style={{ color: '#ef4444', fontSize: 14 }}>Failed to load activity.</div>
      )}
    </div>
  );
}

export default function AdminRestaurantDirectory({ restaurants }) {
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState(null);

  if (selected) {
    return <RestaurantActivity restaurant={selected} onBack={() => setSelected(null)} />;
  }

  const filtered = restaurants.filter(r =>
    r.name.toLowerCase().includes(query.toLowerCase()) ||
    r.username.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h1 style={s.h1}>Directory</h1>
          <p style={s.sub}>{restaurants.length} restaurant{restaurants.length !== 1 ? 's' : ''} on the platform.</p>
        </div>
        <div style={s.searchWrap}>
          <svg style={s.searchIcon} width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            style={s.search}
            type="text"
            placeholder="Search by name or username…"
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
          {query && <button style={s.clearBtn} onClick={() => setQuery('')}>×</button>}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div style={{ color: '#94a3b8', fontSize: 14, padding: '48px 0', textAlign: 'center' }}>
          {query ? `No restaurants matching "${query}"` : 'No restaurants yet.'}
        </div>
      ) : (
        <div style={s.grid}>
          {filtered.map(r => (
            <button key={r.id} style={s.card} onClick={() => setSelected(r)}>
              <div style={s.dot(r.is_active !== 0)} />
              <div style={s.cardName}>{r.name}</div>
              <div style={s.cardUser}>@{r.username}</div>
              {r.is_active === 0 && <span style={s.inactiveBadge}>Inactive</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  h1:   { fontSize: 28, fontWeight: 800, color: '#0f172a', margin: 0, letterSpacing: '-0.5px' },
  sub:  { fontSize: 14, color: '#64748b', marginTop: 4 },

  searchWrap: { position: 'relative', display: 'flex', alignItems: 'center' },
  searchIcon: { position: 'absolute', left: 12, color: '#94a3b8', pointerEvents: 'none' },
  search: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12,
    padding: '9px 36px 9px 34px', fontSize: 13, color: '#0f172a', outline: 'none',
    width: 260, boxShadow: '0 1px 4px rgba(15,23,42,0.06)',
  },
  clearBtn: {
    position: 'absolute', right: 10, background: 'none', border: 'none',
    fontSize: 16, color: '#94a3b8', cursor: 'pointer', lineHeight: 1, padding: 0,
  },

  grid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 },
  card: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 16,
    padding: '18px 20px', boxShadow: '0 1px 4px rgba(15,23,42,0.05)',
    cursor: 'pointer', textAlign: 'left', display: 'flex', flexDirection: 'column', gap: 4,
  },
  dot: active => ({
    width: 7, height: 7, borderRadius: '50%',
    background: active ? '#10b981' : '#cbd5e1', marginBottom: 8,
  }),
  cardName:     { fontSize: 14, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 },
  cardUser:     { fontSize: 12, color: '#94a3b8', fontFamily: 'monospace' },
  inactiveBadge: {
    display: 'inline-block', marginTop: 6, fontSize: 10, fontWeight: 700,
    background: '#fef3c7', color: '#92400e', borderRadius: 5, padding: '2px 7px',
    textTransform: 'uppercase', letterSpacing: '0.05em',
  },

  // Activity view
  backBtn: {
    display: 'flex', alignItems: 'center', gap: 6,
    background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 8,
    padding: '7px 12px', fontSize: 12, fontWeight: 600, color: '#475569', cursor: 'pointer',
    flexShrink: 0,
  },
  dividerV: { width: 1, height: 32, background: '#e2e8f0', flexShrink: 0 },

  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 },
  statCard: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 14,
    padding: '14px 16px', boxShadow: '0 1px 4px rgba(15,23,42,0.05)',
  },
  statVal: { fontSize: 26, fontWeight: 800, color: '#0f172a', marginBottom: 2 },
  statLbl: { fontSize: 11, color: '#94a3b8', fontWeight: 500 },

  listCard: {
    background: '#fff', border: '1px solid #e2e8f0', borderRadius: 18,
    padding: '20px 24px', boxShadow: '0 1px 4px rgba(15,23,42,0.05)',
  },
  ct:   { fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 16 },
  row:  { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, padding: '12px 0' },
  name: { fontSize: 14, fontWeight: 700, color: '#0f172a' },
  badge:{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 5, letterSpacing: '0.03em' },
  meta: { fontSize: 12, color: '#64748b', marginTop: 3, display: 'flex', alignItems: 'center', gap: 6 },
  src:  { background: '#f1f5f9', color: '#64748b', borderRadius: 4, padding: '1px 6px', fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' },
  notes:{ fontSize: 11, color: '#94a3b8', marginTop: 4, fontStyle: 'italic' },
  time: { fontSize: 11, color: '#94a3b8', flexShrink: 0, marginTop: 2 },
};
